import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { assetReportsApi } from '../../../services/api';

const AssetReportsPanel = ({ open, onClose, asset }) => {
  const theme = useTheme();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchReports = useCallback(async () => {
    if (!asset?.id) return;
    setLoading(true);
    try {
      const response = await assetReportsApi.getByAsset(asset.id);
      setReports(response.data.data || []);
    } catch (error) {
      console.error('Error fetching asset reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [asset?.id]);

  useEffect(() => {
    if (open && asset?.id) {
      fetchReports();
    }
  }, [open, asset?.id, fetchReports]);

  const handleView = async (report) => {
    try {
      const response = await assetReportsApi.getDownloadUrl(report.id);
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Error getting report URL:', error);
    }
  };

  const handleDownload = async (report) => {
    try {
      const response = await assetReportsApi.getDownloadUrl(report.id);
      const link = document.createElement('a');
      link.href = response.data.url;
      link.download = response.data.fileName || report.report_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading report:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '70vh' }
      }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        pb: 1, borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Asset Reports
          </Typography>
          <Chip label={reports.length} size="small" color="primary" variant="outlined" />
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : reports.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <AssessmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No reports generated for this asset yet.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {reports.map((report, index) => (
              <React.Fragment key={report.id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ py: 1.5, px: 2.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PdfIcon sx={{ color: '#d32f2f' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {report.report_name}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={report.report_type?.replace(/_/g, ' ')}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.7rem',
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(report.created_at)}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => handleView(report)} sx={{ mr: 0.5 }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => handleDownload(report)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssetReportsPanel;
